import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_INTEGRATIONS,
  GET_AVAILABLE_STORE_INTEGRATIONS,
  CREATE_STORE_INTEGRATION,
  UPDATE_STORE_INTEGRATION,
  DELETE_STORE_INTEGRATION,
} from '../graphql/queries';

// ── Add Integration Modal ──────────────────────────────────────
function AddIntegrationModal({ onClose, onSaved }) {
  const [selectedValue, setSelectedValue] = useState('');
  const [connectorUrl, setConnectorUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');

  const { data, loading: loadingAvailable } = useQuery(GET_AVAILABLE_STORE_INTEGRATIONS, {
    fetchPolicy: 'network-only',
  });
  const [createIntegration, { loading }] = useMutation(CREATE_STORE_INTEGRATION);

  const available = data?.getAvailableStoreIntegrations?.integrations || [];
  const selected = available.find((i) => i.value === selectedValue);
  const needsConnectorUrl = !!selected?.requires?.connectorUrl;
  const needsApiKey = !!selected?.requires?.apiKey;
  const credentialLabel = selected?.credentialLabel || 'API Key';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedValue) { setError('Select an integration'); return; }
    if (needsConnectorUrl && !connectorUrl.trim()) {
      setError(`Connector URL is required for ${selected.name}`);
      return;
    }
    if (needsApiKey && !apiKey.trim()) {
      setError(`${credentialLabel} is required for ${selected.name}`);
      return;
    }

    try {
      const { data: res } = await createIntegration({
        variables: {
          value: selectedValue,
          connectorUrl: needsConnectorUrl ? connectorUrl.trim() : null,
          apiKey: needsApiKey ? apiKey.trim() : null,
        },
      });
      if (res?.createStoreIntegration?.error) {
        setError(res.createStoreIntegration.message);
        return;
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to add integration');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Add Integration</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Integration</label>
            {loadingAvailable ? (
              <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</p>
            ) : available.length === 0 ? (
              <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>All integrations already added</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {available.map((item) => (
                  <label
                    key={item.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '0.6rem 0.75rem',
                      border: selectedValue === item.value ? '2px solid #6366f1' : '1px solid #e2e8f0',
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      background: selectedValue === item.value ? '#6366f108' : '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="integration"
                      value={item.value}
                      checked={selectedValue === item.value}
                      onChange={(e) => setSelectedValue(e.target.value)}
                      style={{ accentColor: '#6366f1' }}
                    />
                    {item.logo && (
                      <img
                        src={item.logo}
                        alt=""
                        style={{ width: 24, height: 24, objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span style={{ fontWeight: 500, fontSize: '0.92rem' }}>{item.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {needsConnectorUrl && (
            <div className="form-group">
              <label className="form-label">Connector URL <span className="form-required">*</span></label>
              <input
                className="form-input"
                value={connectorUrl}
                onChange={(e) => setConnectorUrl(e.target.value)}
                placeholder="https://…"
                required
              />
            </div>
          )}

          {needsApiKey && (
            <div className="form-group">
              <label className="form-label">{credentialLabel} <span className="form-required">*</span></label>
              <input
                className="form-input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={`Enter ${credentialLabel.toLowerCase()}`}
                required
              />
            </div>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || available.length === 0}
            >
              {loading ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Integration Modal ─────────────────────────────────────
function EditIntegrationModal({ integration, onClose, onSaved }) {
  const [connectorUrl, setConnectorUrl] = useState(integration.connectorUrl || '');
  const [apiKey, setApiKey] = useState(integration.apiKey || '');
  const [error, setError] = useState('');

  const [updateIntegration, { loading }] = useMutation(UPDATE_STORE_INTEGRATION);

  // Only message is supported via the admin panel today, but the form is
  // shaped generically in case the whitelist grows.
  const showConnectorUrl = !!integration.connectorUrl;
  const showApiKey = integration.value === 'message';
  const credentialLabel = 'API Key';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const variables = { id: integration.id };
      if (showConnectorUrl) variables.connectorUrl = connectorUrl.trim();
      if (showApiKey) variables.apiKey = apiKey.trim();

      const { data: res } = await updateIntegration({ variables });
      if (res?.updateStoreIntegration?.error) {
        setError(res.updateStoreIntegration.message);
        return;
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to update integration');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Edit {integration.name}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {showConnectorUrl && (
            <div className="form-group">
              <label className="form-label">Connector URL</label>
              <input
                className="form-input"
                value={connectorUrl}
                onChange={(e) => setConnectorUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
          )}

          {showApiKey && (
            <div className="form-group">
              <label className="form-label">{credentialLabel}</label>
              <input
                className="form-input"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={`Enter ${credentialLabel.toLowerCase()}`}
              />
            </div>
          )}

          {!showConnectorUrl && !showApiKey && (
            <p style={{ color: '#64748b', fontSize: '0.88rem' }}>
              No editable fields for this integration type.
            </p>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete Confirm Modal ───────────────────────────────────────
function DeleteIntegrationModal({ integration, onClose, onDeleted }) {
  const [deleteIntegration, { loading }] = useMutation(DELETE_STORE_INTEGRATION);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setError('');
    try {
      const { data } = await deleteIntegration({ variables: { id: integration.id } });
      if (data?.deleteStoreIntegration?.error) {
        setError(data.deleteStoreIntegration.message);
        return;
      }
      onDeleted();
    } catch (err) {
      setError(err.message || 'Failed to remove');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Remove Integration</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p style={{ color: '#475569', marginBottom: '0.5rem' }}>
            Remove <strong style={{ color: '#1e293b' }}>{integration.name}</strong> from your store?
          </p>
          <p style={{ fontSize: '0.82rem', color: '#94a3b8' }}>You can re-add it any time.</p>
          {error && <div className="form-error" style={{ marginTop: '0.75rem' }}>{error}</div>}
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn"
              onClick={handleDelete}
              disabled={loading}
              style={{ background: '#f43f5e', color: '#fff', border: 'none' }}
            >
              {loading ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function Integrations() {
  const { data, loading, refetch } = useQuery(GET_INTEGRATIONS, { fetchPolicy: 'network-only' });
  const integrations = data?.getIntegrations?.integrations || [];

  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const handleSaved = () => {
    setShowAdd(false);
    setEditItem(null);
    refetch();
  };
  const handleDeleted = () => {
    setDeleteItem(null);
    refetch();
  };

  return (
    <>
      <header className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Integrations</h1>
          <p className="dashboard-subtitle">Manage your connected integrations</p>
        </div>
        <div className="header-actions">
          <button className="btn btn--primary" onClick={() => setShowAdd(true)}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 16, height: 16 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Integration
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        {loading ? (
          <div className="settings-loading">Loading...</div>
        ) : integrations.length === 0 ? (
          <div className="empty-state" style={{ marginTop: '3rem' }}>
            <div className="empty-state-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <p className="empty-state-title">No integrations</p>
            <p className="empty-state-desc">Tap "Add Integration" to connect one to your store.</p>
          </div>
        ) : (
          <div className="integration-table-wrap">
            <table className="integration-table">
              <thead>
                <tr>
                  <th>Integration</th>
                  <th>Connector URL</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {integrations.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="integration-name-cell">
                        {item.logo && (
                          <img
                            src={item.logo}
                            alt={item.name}
                            className="integration-logo"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span className="integration-name">{item.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="integration-url">
                        {item.connectorUrl || <span style={{ color: 'var(--slate-500)' }}>—</span>}
                      </span>
                    </td>
                    <td>
                      {item.connected ? (
                        <span className="integration-badge integration-badge--connected">
                          <span className="integration-badge-dot" />
                          Connected
                        </span>
                      ) : (
                        <span className="integration-badge integration-badge--disconnected">
                          <span className="integration-badge-dot" />
                          Disconnected
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
                        <button
                          className="btn btn--ghost"
                          style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem' }}
                          onClick={() => setEditItem(item)}
                        >Edit</button>
                        <button
                          className="btn"
                          style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem', background: '#f43f5e10', color: '#f43f5e', border: '1px solid #f43f5e30' }}
                          onClick={() => setDeleteItem(item)}
                        >Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showAdd && <AddIntegrationModal onClose={() => setShowAdd(false)} onSaved={handleSaved} />}
      {editItem && <EditIntegrationModal integration={editItem} onClose={() => setEditItem(null)} onSaved={handleSaved} />}
      {deleteItem && <DeleteIntegrationModal integration={deleteItem} onClose={() => setDeleteItem(null)} onDeleted={handleDeleted} />}
    </>
  );
}
